import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Breadcrumb, Modal, Row, Col, Button } from 'antd';

import { Utils } from '../../utils';
import { useGetShow } from '../hooks/query/show';
import { useGetEpisode } from '../hooks/query/episode';
import { useGetNetwork, useGetNetworks } from '../hooks/query/network';
import useGetMarketplaceProfile from '../hooks/query/marketplace/profile/useGetMarketplaceProfile';
import Loader from './Loader';

const NavigationBreadcrumb = ({ activeLabel, withConfirm }) => {
  const [cancelModalIsVisible, setCancelModalIsVisible] = useState(false);

  const { isNetworksLoading, hasMultipleNetworks } = useGetNetworks({ options: { limit: 2 } });
  const { network: currentNetwork, isNetworkLoading } = useGetNetwork();

  const { show, isShowLoading } = useGetShow();
  const { episode, isEpisodeLoading } = useGetEpisode();
  const { profile, isProfileLoading } = useGetMarketplaceProfile();

  const isLoading =
    isShowLoading || isEpisodeLoading || isNetworksLoading || isNetworkLoading || isProfileLoading;

  const items = useMemo(
    () => [
      {
        // Home
        link: `/networks`,
        label: (
          <span className="breadcrumb-mute">
            <FormattedMessage defaultMessage="Overview" />
          </span>
        ),
        enabled: true,
      },
      {
        // Network
        link: `/networks/${currentNetwork?._id}`,
        label: (
          <span className="breadcrumb-mute">{Utils.truncate(currentNetwork?.name, 24, '...')}</span>
        ),
        enabled: hasMultipleNetworks && !!currentNetwork,
      },
      {
        // Show
        link: `/networks/${currentNetwork?._id}/shows/${show?._id}${window.location.search}`,
        label: (
          <span className={episode || profile || activeLabel ? 'breadcrumb-mute' : ''}>
            {Utils.truncate(show?.title, 24, '...')}
          </span>
        ),
        enabled: !!show && !!currentNetwork,
      },
      {
        // Episode
        link: `/networks/${currentNetwork?._id}/shows/${show?._id}/episodes/${episode?._id}${window.location.search}`,
        label: (
          <span className={activeLabel || profile ? 'breadcrumb-mute' : ''}>
            {episode?.title ? (
              Utils.truncate(episode?.title, 24, '...')
            ) : (
              <FormattedMessage defaultMessage="Draft episode" />
            )}
          </span>
        ),
        enabled: !!episode && !!show && !!currentNetwork,
      },
      {
        // profile
        link: null,
        label: (
          <span className={activeLabel ? 'breadcrumb-mute' : ''}>
            {profile?.name ? (
              Utils.truncate(profile?.name, 24, '...')
            ) : (
              <FormattedMessage defaultMessage="Podcast profile" />
            )}
          </span>
        ),
        enabled: !!profile,
      },
      {
        // label
        link: null,
        label: activeLabel,
        enabled: !!activeLabel,
      },
    ],
    [activeLabel, currentNetwork, episode, hasMultipleNetworks, profile, show]
  );

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <Breadcrumb
        items={items
          .filter((item) => item.enabled)
          .map(({ link, label }) => {
            return {
              title: link ? (
                <>
                  {withConfirm ? (
                    <a onClick={() => setCancelModalIsVisible(link)}>{label}</a>
                  ) : (
                    <Link to={link + window.location.search}>{label}</Link>
                  )}
                </>
              ) : (
                label
              ),
            };
          })}
      />

      <Modal
        closable={false}
        open={cancelModalIsVisible}
        title={<FormattedMessage defaultMessage="Are you sure?" />}
        footer={
          <Row justify="space-between" gutter={[16, 16]}>
            <Col>
              <Button
                key="back"
                type="text"
                onClick={() => {
                  setCancelModalIsVisible(false);
                }}
              >
                <FormattedMessage defaultMessage="Keep editing" />
              </Button>
            </Col>
            <Col>
              <Link to={cancelModalIsVisible}>
                <Button danger>
                  <FormattedMessage defaultMessage="Leave and discard changes" />
                </Button>
              </Link>
            </Col>
          </Row>
        }
      >
        <FormattedMessage defaultMessage="Any unsaved changes will be discarded." />
      </Modal>
    </>
  );
};

NavigationBreadcrumb.propTypes = {
  withConfirm: PropTypes.bool,
  activeLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default NavigationBreadcrumb;
